/**
 * LoanActivityFeedItem — one avatar-led entry in a loan activity feed.
 *
 * Shared by the Summary tab's "Recent activity" panel (summaryShared.js) and the
 * full Activity Log sidebar (LoanChangelog.jsx) so both read identically:
 *
 *   [avatar]  **Name** did-something · time
 *             ┌ comment / condition body (rich, collapsible) ┐
 *             │ document rows (previewable when clicked)      │
 *             └ from → to status transition                   ┘
 *
 * All wording/icons come from loanChangelogConfig, so a condition, comment, note,
 * upload, or status change all render through the same helpers. Dates use the
 * shared timezone-correct formatDateTime (Eastern) per the app-wide dates rule.
 *
 * Props:
 *   ev          raw audit event
 *   onPreview   (doc) => void — open a previewable document
 *   isLast      drop the bottom divider on the final row
 *   expandable  when true, the row toggles a Details / JSON panel (audit log)
 */
import {
  DataObjectOutlined,
  ExpandMoreOutlined,
  ImageOutlined,
  InsertDriveFileOutlined,
  OpenInNewOutlined,
} from "@mui/icons-material";
import { Box, Collapse } from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";

import { formatDateTime } from "../../../../../_src_shared/utils/functions/formatDateTime";
import Flx from "../../../../../components/layout/Flx";
import Txt from "../../../../../components/typography/Txt";
import JsonPreview from "../../../../../components/common/JsonPreview";
import {
  actionPhrase,
  commentHtml,
  detailFields,
  eventDocuments,
  FIELD_LABELS,
  fmtVal,
  hueFor,
  initials,
  metaFor,
  statusTransition,
  summaryLine,
} from "./loanChangelogConfig";

const C = {
  ink: "#16242b",
  sub: "#5d7079",
  faint: "#98a4ac",
  line: "#e7ecef",
  teal: "#14b8a6",
  tealText: "#0d9488",
  tealBg: "#e6f6f4",
  cardBg: "#f7f9fa",
};

const stop = (e) => e.stopPropagation();

// Short lowercase time, e.g. "1:38 pm".
// Pass the RAW backend ISO straight to formatDateTime — audit timestamps are
// naive UTC ("2026-06-17T17:29:45.669102", no zone suffix) and formatDateTime
// appends the 'Z' itself. Round-tripping through `new Date(iso).toISOString()`
// would parse the naive string as LOCAL time first, shifting every event time
// by the viewer's UTC offset (4-5h late in Eastern).
const feedTime = (iso) => formatDateTime(iso, "time").toLowerCase();

const isImageDoc = (d) =>
  /^image\//.test(d?.content_type || "") ||
  /\.(png|jpe?g|gif|webp|bmp|svg|heic|tiff?)$/i.test(d?.file_display_name || "");

// Resolve a (possibly sparse) event document against the loan's documents by
// _id — metadata-edit events only carry the id + changed value, so the display
// name / group / type come from the matched loan document.
const resolveDoc = (doc, loanDocs) => {
  const r = Array.isArray(loanDocs)
    ? loanDocs.find((d) => d._id === doc._id || d.oid === doc._id)
    : null;
  return {
    ...doc,
    file_display_name:
      r?.file_display_name || doc.file_display_name || doc.docType || "Document",
    docGroup: r?.docGroup || doc.docGroup || null,
    docType: r?.docType || doc.docType || null,
    content_type: r?.content_type || doc.content_type || null,
    // Prefer the loan doc's ids so a preview can be fetched from anywhere.
    dealId: r?.dealId || doc.dealId || null,
    containerName: r?.containerName || doc.containerName || null,
    file_storage_name: r?.file_storage_name || doc.file_storage_name || null,
  };
};

// Light status chip for a document-status change (Accepted, Rejected, …).
const STATUS_TONE = {
  Accepted: { fg: "#166534", bg: "#e9f7ee" },
  Approved: { fg: "#166534", bg: "#e9f7ee" },
  Rejected: { fg: "#b42318", bg: "#fdecec" },
  Incomplete: { fg: "#b42318", bg: "#fdecec" },
  "Pending Review": { fg: "#92600c", bg: "#fbf3e4" },
  "New Upload": { fg: "#1d4ed8", bg: "#eaf0fe" },
};
const DocStatusChip = ({ status }) => {
  const t = STATUS_TONE[status] || { fg: C.sub, bg: "#eef2f4" };
  return (
    <Box
      component="span"
      sx={{
        fontSize: 10.5,
        fontWeight: 700,
        color: t.fg,
        background: t.bg,
        px: "8px",
        py: "2px",
        borderRadius: "999px",
        whiteSpace: "nowrap",
        flexShrink: 0,
      }}
    >
      {status}
    </Box>
  );
};

// Deterministically tinted circular initials avatar.
const FeedAvatar = ({ name }) => {
  const h = hueFor(name || "?");
  return (
    <Box
      sx={{
        width: 28,
        height: 28,
        flexShrink: 0,
        borderRadius: "50%",
        display: "grid",
        placeItems: "center",
        fontSize: 10.5,
        fontWeight: 700,
        letterSpacing: "0.02em",
        color: `hsl(${h}, 42%, 40%)`,
        background: `hsl(${h}, 52%, 92%)`,
      }}
    >
      {initials(name) || "?"}
    </Box>
  );
};

// A natural, lowercase header phrase ("commented", "created a condition",
// "updated 3 documents", "changed status", "edited deal terms").
const feedPhrase = (ev, docCount) => {
  if (ev?.event_type === "comments") return "commented";
  if (ev?.event_type === "deal_terms") return "edited deal terms";
  if (statusTransition(ev) || ev?.event_type === "loan_stage")
    return "changed status";
  if (docCount > 0) {
    const verb = /upload/i.test(ev?.sub_event || "") ? "uploaded" : "updated";
    return `${verb} ${docCount} document${docCount === 1 ? "" : "s"}`;
  }
  const phrase = actionPhrase(ev) || "made an update";
  return phrase.charAt(0).toLowerCase() + phrase.slice(1);
};

// Rich comment / condition body with a collapse-to-N-lines "Show full comment".
const CommentBody = ({ html }) => {
  const ref = useRef(null);
  const [expanded, setExpanded] = useState(false);
  const [overflowing, setOverflowing] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (el) setOverflowing(el.scrollHeight > el.clientHeight + 2);
  }, [html]);

  return (
    <Box
      onClick={stop}
      sx={{
        mt: "6px",
        p: "9px 12px",
        background: "#fff",
        border: `1px solid ${C.line}`,
        borderLeft: `3px solid ${C.teal}`,
        borderRadius: "9px",
        cursor: "text",
      }}
    >
      <Box
        ref={ref}
        dangerouslySetInnerHTML={{ __html: html }}
        sx={{
          fontSize: 12.5,
          lineHeight: 1.5,
          color: "#344054",
          wordBreak: "break-word",
          ...(expanded
            ? {}
            : {
                display: "-webkit-box",
                WebkitLineClamp: 4,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }),
          "& p": { m: "0 0 5px" },
          "& p:last-child": { mb: 0 },
          "& ol, & ul": { m: "4px 0", pl: 2.25 },
          "& li": { my: 0.25 },
          "& li p": { m: 0 },
          "& a": { color: C.tealText, textDecoration: "none" },
          "& .wysiwyg-mention": {
            color: C.tealText,
            fontWeight: 600,
            background: C.tealBg,
            px: "3px",
            borderRadius: "3px",
          },
        }}
      />
      {(overflowing || expanded) && (
        <Box
          role="button"
          onClick={() => setExpanded((v) => !v)}
          sx={{
            mt: "8px",
            display: "inline-flex",
            fontSize: 11.5,
            fontWeight: 600,
            color: C.sub,
            cursor: "pointer",
            px: "10px",
            py: "4px",
            borderRadius: "7px",
            border: `1px solid ${C.line}`,
            background: "#fff",
            "&:hover": { background: C.cardBg },
          }}
        >
          {expanded ? "Show less" : "Show full comment"}
        </Box>
      )}
    </Box>
  );
};

// camelCase / snake_case field key -> readable label, preferring the curated
// FIELD_LABELS map and falling back to a de-camelCased title.
const prettifyField = (k) =>
  FIELD_LABELS[k] ||
  String(k)
    .replace(/_/g, " ")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/^./, (c) => c.toUpperCase());

// Structured "what changed" box for field-edit events (e.g. loan details,
// deal terms): one row per changed field, label + formatted value. Internal
// bookkeeping keys (updateDateTime, etc.) are already filtered by detailFields.
const FieldChangeBox = ({ fields }) => (
  <Box
    sx={{
      mt: "8px",
      px: "12px",
      background: "#fff",
      border: `1px solid ${C.line}`,
      borderRadius: "9px",
    }}
  >
    {fields.map(([k, v], i) => (
      <Flx
        key={k}
        ac
        g={1.5}
        sx={{
          py: "7px",
          borderBottom: i < fields.length - 1 ? `1px solid ${C.cardBg}` : "none",
        }}
      >
        <Txt sx={{ fontSize: 12, color: C.sub, whiteSpace: "nowrap" }}>
          {prettifyField(k)}
        </Txt>
        <Txt
          sx={{
            fontSize: 12,
            fontWeight: 600,
            color: C.ink,
            textAlign: "right",
            wordBreak: "break-word",
            ml: "auto",
          }}
        >
          {fmtVal(v)}
        </Txt>
      </Flx>
    ))}
  </Box>
);

// One document row: type icon, filename, right-aligned group tag. Clickable to
// preview when the doc resolves to a file (has an _id).
const FeedDocRow = ({ doc: rawDoc, loanDocuments, onPreview }) => {
  const doc = useMemo(
    () => resolveDoc(rawDoc, loanDocuments),
    [rawDoc, loanDocuments],
  );
  const clickable = doc.previewable && typeof onPreview === "function";
  const Icon = isImageDoc(doc) ? ImageOutlined : InsertDriveFileOutlined;
  // Surface both classification levels when present: "Group · Type".
  const meta = [doc.docGroup, doc.docType].filter(Boolean).join(" · ");
  return (
    <Flx
      ac
      g={1}
      onClick={
        clickable
          ? (e) => {
              e.stopPropagation();
              onPreview(doc);
            }
          : undefined
      }
      sx={{
        px: "9px",
        py: "7px",
        borderRadius: "8px",
        border: `1px solid ${C.line}`,
        background: "#fff",
        cursor: clickable ? "pointer" : "default",
        transition: "background .13s, border-color .13s",
        ...(clickable && {
          "&:hover": { background: C.cardBg, borderColor: "#c9d5db" },
        }),
      }}
    >
      <Icon sx={{ fontSize: 16, color: C.sub, flexShrink: 0 }} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Txt
          sx={{
            fontSize: 12,
            fontWeight: 600,
            color: C.ink,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {doc.file_display_name}
        </Txt>
        {(meta || doc.documentStatus) && (
          <Txt
            sx={{
              fontSize: 11,
              color: C.faint,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {meta}
            {doc.documentStatus && (
              <>
                {meta ? " · " : ""}status set to{" "}
                <Box component="span" sx={{ fontWeight: 600, color: C.sub }}>
                  {doc.documentStatus}
                </Box>
              </>
            )}
          </Txt>
        )}
      </Box>
      {doc.documentStatus && <DocStatusChip status={doc.documentStatus} />}
      {clickable && (
        <OpenInNewOutlined sx={{ fontSize: 13, color: C.faint, flexShrink: 0 }} />
      )}
    </Flx>
  );
};

const ActivityFeedItem = ({
  ev,
  onPreview,
  isLast,
  expandable = false,
  loanDocuments,
}) => {
  const [open, setOpen] = useState(false);

  const name = ev?.changed_by_name || "Someone";
  const first = name.split(" ")[0];
  const comment = commentHtml(ev);
  const docs = useMemo(() => eventDocuments(ev), [ev]);
  const transition = statusTransition(ev);
  const fields = useMemo(() => detailFields(ev), [ev]);
  // The friendly "what changed" box: field edits with no richer representation.
  // Suppressed when the event already renders documents (uploads / doc edits) —
  // there we only want the document rows, not the raw dealId/source/count fields.
  const showFields =
    !comment && !transition && !docs.length && fields.length > 0;
  const summary =
    !comment && !docs.length && !transition && !showFields
      ? summaryLine(ev)
      : "";
  const phrase = feedPhrase(ev, docs.length);
  const m = metaFor(ev?.event_type);

  return (
    <Flx
      g={1.25}
      sx={{
        py: "12px",
        borderBottom: isLast ? "none" : `1px solid ${C.line}`,
      }}
    >
      <FeedAvatar name={name} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {/* header: who / what / when (clickable to expand in audit-log mode) */}
        <Flx
          ac
          g={0.75}
          onClick={expandable ? () => setOpen((o) => !o) : undefined}
          sx={{
            cursor: expandable ? "pointer" : "default",
            userSelect: expandable ? "none" : "auto",
          }}
        >
          <Txt sx={{ flex: 1, minWidth: 0, fontSize: 12.5, color: C.sub, lineHeight: 1.4 }}>
            <Box component="span" sx={{ fontWeight: 700, color: C.ink }}>
              {first}
            </Box>{" "}
            {phrase}
            <Box component="span" sx={{ color: C.faint }}>
              {"  ·  "}
              {feedTime(ev?.timestamp)}
            </Box>
          </Txt>
          {expandable && (
            <ExpandMoreOutlined
              sx={{
                fontSize: 16,
                color: "#c3c8d4",
                flexShrink: 0,
                transition: "transform .16s ease",
                transform: open ? "rotate(180deg)" : "none",
              }}
            />
          )}
        </Flx>

        {summary && (
          <Txt sx={{ fontSize: 12, color: C.sub, mt: "2px" }}>{summary}</Txt>
        )}

        {transition && (
          <Flx ac g={0.75} wrap sx={{ mt: "6px" }}>
            {transition.from && (
              <>
                <Box
                  component="span"
                  sx={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: C.sub,
                    background: "#eef2f4",
                    px: "8px",
                    py: "2px",
                    borderRadius: "999px",
                  }}
                >
                  {transition.from}
                </Box>
                <Box component="span" sx={{ fontSize: 12, color: C.faint }}>
                  →
                </Box>
              </>
            )}
            <Box
              component="span"
              sx={{
                fontSize: 11,
                fontWeight: 700,
                color: m.accent,
                background: m.tint,
                px: "8px",
                py: "2px",
                borderRadius: "999px",
              }}
            >
              {transition.to}
            </Box>
          </Flx>
        )}

        {comment && <CommentBody html={comment} />}

        {showFields && <FieldChangeBox fields={fields} />}

        {docs.length > 0 && (
          <Flx column g={0.75} sx={{ mt: "8px" }}>
            {docs.map((d) => (
              <FeedDocRow
                key={d.key}
                doc={d}
                loanDocuments={loanDocuments}
                onPreview={onPreview}
              />
            ))}
          </Flx>
        )}

        {/* audit-log raw event (sidebar only) */}
        {expandable && (
          <Collapse in={open} unmountOnExit>
            <Box onClick={stop} sx={{ mt: 1, pt: 1, borderTop: `1px dashed ${C.line}` }}>
              <Flx ac g={0.5} sx={{ mb: 0.75, color: C.faint }}>
                <DataObjectOutlined sx={{ fontSize: 14 }} />
                <Txt sx={{ fontSize: 11, fontWeight: 600 }}>Raw event</Txt>
              </Flx>
              <Box
                sx={{
                  background: "#fff",
                  border: `1px solid ${C.line}`,
                  borderRadius: 2,
                  p: "8px 10px",
                  maxHeight: 320,
                  overflow: "auto",
                }}
              >
                <JsonPreview
                  values={ev}
                  sticky={false}
                  showCollapseControls={false}
                  defaultCollapse={2}
                />
              </Box>
            </Box>
          </Collapse>
        )}
      </Box>
    </Flx>
  );
};

export default ActivityFeedItem;
